import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { Database } from '../types/database.types';
import {
  Tag, Plus, Search, X, Edit3, Trash2, ShoppingCart, Save,
  Camera, Sparkles, Sun, Move, Palette, Clock, ShieldCheck,
  CheckCircle2, AlertCircle, Code2, Wand2, Loader2,
  ChevronDown, ChevronRight, EyeOff, RotateCcw, Upload, Film, Video
} from 'lucide-react';
import ModalOverlay from '../components/ui/ModalOverlay';
import { MARKETPLACE_ENABLED } from '../lib/featureFlags';

type SceneTag = Database['public']['Tables']['scene_tags']['Row'];

const CATEGORY_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  camera: { icon: <Camera size={12} />, color: '#6366f1', label: 'Câmera' },
  transformation: { icon: <Sparkles size={12} />, color: '#a855f7', label: 'Transição' },
  lighting: { icon: <Sun size={12} />, color: '#f59e0b', label: 'Iluminação' },
  movement: { icon: <Move size={12} />, color: '#06b6d4', label: 'Movimento' },
  style: { icon: <Palette size={12} />, color: '#ec4899', label: 'Estilo' },
  timing: { icon: <Clock size={12} />, color: '#10b981', label: 'Timing' },
  constraints: { icon: <ShieldCheck size={12} />, color: '#ef4444', label: 'Restrições' },
  distance: { icon: <Move size={12} />, color: '#f43f5e', label: 'Distância' },
};

const ICON_MAP: Record<string, React.ReactNode> = {
  camera: <Camera size={14} />,
  sparkles: <Sparkles size={14} />,
  sun: <Sun size={14} />,
  move: <Move size={14} />,
  palette: <Palette size={14} />,
  clock: <Clock size={14} />,
  'shield-check': <ShieldCheck size={14} />,
  tag: <Tag size={14} />,
};

const DEFAULT_PROMPT_JSON = {
  instruction: '',
  parameters: {},
  weight: 1.0,
  conflicts_with: [] as string[],
};

export default function TagManagement() {
  const { user } = useAuth();
  const { org, role } = useOrg();
  const [tags, setTags] = useState<SceneTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editingTag, setEditingTag] = useState<SceneTag | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [jsonViewTag, setJsonViewTag] = useState<SceneTag | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [generatingJson, setGeneratingJson] = useState(false);
  const [promptDescription, setPromptDescription] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [hiddenSystemTagIds, setHiddenSystemTagIds] = useState<Set<string>>(new Set());

  // Marketplace Publish Modal State
  const [publishingTag, setPublishingTag] = useState<SceneTag | null>(null);
  const [publishPrice, setPublishPrice] = useState('3');
  const [publishMediaSource, setPublishMediaSource] = useState<'none' | 'gallery' | 'upload'>('none');
  const [publishSelectedAsset, setPublishSelectedAsset] = useState<string>('');
  const [publishUploadFile, setPublishUploadFile] = useState<File | null>(null);
  const [orgAssets, setOrgAssets] = useState<any[]>([]);

  const [deletingTag, setDeletingTag] = useState<SceneTag | null>(null);

  const [form, setForm] = useState({
    tag_key: '',
    category: 'camera',
    label: '',
    icon_name: 'tag',
    color: '#6366f1',
    prompt_json: JSON.stringify(DEFAULT_PROMPT_JSON, null, 2),
  });

  const isAdmin = role === 'admin';

  const fetchTags = useCallback(async () => {
    setLoading(true);
    // Fetch system tags + org tags
    const query = supabase.from('scene_tags').select('*').eq('is_active', true).order('category').order('label');
    const { data } = await query;
    setTags(data || []);

    // Fetch hidden tags for this org
    if (org?.id) {
      const { data: hiddenRows } = await supabase
        .from('org_hidden_tags')
        .select('tag_id')
        .eq('org_id', org.id);
      if (hiddenRows) {
        setHiddenSystemTagIds(new Set(hiddenRows.map((r: any) => r.tag_id)));
      }
    }
    setLoading(false);
  }, [org?.id]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleHideSystemTag = async (tag: SceneTag) => {
    if (!org?.id || !user?.id) return;
    try {
      const { error } = await supabase.from('org_hidden_tags').insert({
        org_id: org.id,
        tag_id: tag.id,
        hidden_by: user.id,
      });
      if (error) throw error;
      setHiddenSystemTagIds(prev => {
        const next = new Set(Array.from(prev));
        next.add(tag.id);
        return next;
      });
      setFeedback({ type: 'success', message: `"${tag.label}" ocultada para esta organização.` });
    } catch (err: any) {
      if (err.code === '23505') {
        setFeedback({ type: 'error', message: 'Tag já está oculta.' });
      } else {
        setFeedback({ type: 'error', message: err.message });
      }
    }
  };

  const handleRestoreSystemTag = async (tagId: string) => {
    if (!org?.id) return;
    try {
      const { error } = await supabase.from('org_hidden_tags')
        .delete()
        .eq('org_id', org.id)
        .eq('tag_id', tagId);
      if (error) throw error;
      setHiddenSystemTagIds(prev => {
        const next = new Set(Array.from(prev));
        next.delete(tagId);
        return next;
      });
      setFeedback({ type: 'success', message: 'Tag restaurada com sucesso.' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const handleRestoreAllSystemTags = async () => {
    if (!org?.id) return;
    try {
      const { error } = await supabase.from('org_hidden_tags')
        .delete()
        .eq('org_id', org.id);
      if (error) throw error;
      setHiddenSystemTagIds(new Set());
      setFeedback({ type: 'success', message: 'Todas as tags do sistema foram restauradas.' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const fetchOrgAssets = useCallback(async () => {
    if (!org?.id) return;
    const { data } = await supabase
      .from('media_assets')
      .select('id, video_url, created_at, status')
      .eq('org_id', org.id)
      .eq('status', 'approved')
      .order('created_at', { ascending: false });
    if (data) setOrgAssets(data);
  }, [org?.id]);

  useEffect(() => {
    if (publishingTag && publishMediaSource === 'gallery' && orgAssets.length === 0) {
      fetchOrgAssets();
    }
  }, [publishingTag, publishMediaSource, orgAssets.length, fetchOrgAssets]);

  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 4000);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  const filteredTags = tags.filter(t => {
    const matchSearch = !search ||
      t.label.toLowerCase().includes(search.toLowerCase()) ||
      t.tag_key.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || t.category === filterCat;
    return matchSearch && matchCat;
  });

  const allSystemTags = filteredTags.filter(t => t.is_system);
  const visibleSystemTags = allSystemTags.filter(t => !hiddenSystemTagIds.has(t.id));
  const hiddenSystemTags = allSystemTags.filter(t => hiddenSystemTagIds.has(t.id));
  const orgTags = filteredTags.filter(t => !t.is_system);

  const openEditor = (tag?: SceneTag) => {
    if (tag) {
      setEditingTag(tag);
      setForm({
        tag_key: tag.tag_key,
        category: tag.category,
        label: tag.label,
        icon_name: tag.icon_name,
        color: tag.color,
        prompt_json: JSON.stringify(tag.prompt_json, null, 2),
      });
    } else {
      setEditingTag(null);
      setForm({
        tag_key: '',
        category: 'camera',
        label: '',
        icon_name: 'tag',
        color: '#6366f1',
        prompt_json: JSON.stringify(DEFAULT_PROMPT_JSON, null, 2),
      });
    }
    setShowEditor(true);
  };

  const handleGenerateWithAI = async () => {
    if (!promptDescription.trim()) {
      setFeedback({ type: 'error', message: 'Descreva o que a tag deve fazer.' });
      return;
    }
    setGeneratingJson(true);
    try {
      const { data: rawData, error: fnError } = await supabase.functions.invoke('generate-tag-json', {
        body: { description: promptDescription }
      });
      console.log('generate-tag-json raw response:', rawData, 'error:', fnError);
      if (fnError) throw new Error(fnError.message);
      
      // Handle both string and object responses
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      console.log('generate-tag-json parsed data:', data);
      
      if (!data?.success) throw new Error(data?.error || 'Erro ao gerar JSON.');

      setForm(prev => ({
        ...prev,
        tag_key: data.tag_key || prev.tag_key,
        category: data.category || prev.category,
        label: data.label || prev.label,
        icon_name: data.icon_name || prev.icon_name,
        color: data.color || prev.color,
        prompt_json: JSON.stringify(data.prompt_json, null, 2),
      }));
      setFeedback({ type: 'success', message: 'JSON gerado pela IA! Revise e ajuste se necessário.' });
    } catch (err: any) {
      console.error('generate-tag-json error:', err);
      setFeedback({ type: 'error', message: err.message || 'Erro desconhecido ao gerar JSON.' });
    } finally {
      setGeneratingJson(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org || !user) return;

    let parsedJson;
    try {
      parsedJson = JSON.parse(form.prompt_json);
    } catch {
      setFeedback({ type: 'error', message: 'JSON do prompt inválido. Verifique a formatação.' });
      return;
    }

    try {
      if (editingTag) {
        const { error } = await supabase.from('scene_tags').update({
          label: form.label,
          category: form.category,
          icon_name: form.icon_name,
          color: form.color,
          prompt_json: parsedJson,
        }).eq('id', editingTag.id);
        if (error) throw error;
        setFeedback({ type: 'success', message: 'Tag atualizada!' });
      } else {
        const { error } = await supabase.from('scene_tags').insert({
          tag_key: form.tag_key,
          category: form.category,
          label: form.label,
          icon_name: form.icon_name,
          color: form.color,
          prompt_json: parsedJson,
          org_id: org.id,
          author_id: user.id,
          is_system: false,
        });
        if (error) throw error;
        setFeedback({ type: 'success', message: 'Tag criada com sucesso!' });
      }
      setShowEditor(false);
      fetchTags();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const handleDelete = (tag: SceneTag) => {
    setDeletingTag(tag);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTag || !org) return;
    try {
      const { error: deleteError } = await supabase.from('scene_tags').delete().eq('id', deletingTag.id);
      if (deleteError) throw deleteError;

      // Ensure purchases are kept by just setting is_active to false in the marketplace
      const { error: marketError } = await supabase
        .from('marketplace_items')
        .update({ is_active: false })
        .eq('org_id', org.id)
        .eq('type', 'scene_tag')
        .contains('tags', [deletingTag.tag_key]);
        
      if (marketError) console.error('Error updating marketplace on delete:', marketError);

      setFeedback({ type: 'success', message: 'Tag removida com sucesso.' });
      fetchTags();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setDeletingTag(null);
    }
  };

  const handlePublishToMarketplace = (tag: SceneTag) => {
    setPublishingTag(tag);
    setPublishPrice('3');
    setPublishMediaSource('none');
    setPublishSelectedAsset('');
    setPublishUploadFile(null);
  };

  const handleConfirmPublish = async () => {
    if (!org || !user || !publishingTag) return;
    const priceNum = parseInt(publishPrice);
    if (isNaN(priceNum) || priceNum < 1) {
      setFeedback({ type: 'error', message: 'Preço inválido.' });
      return;
    }

    setPublishingId(publishingTag.id);
    let thumbnailUrl = null;

    try {
      if (publishMediaSource === 'gallery' && publishSelectedAsset) {
        const asset = orgAssets.find(a => a.id === publishSelectedAsset);
        if (asset) thumbnailUrl = asset.video_url;
      } else if (publishMediaSource === 'upload' && publishUploadFile) {
        const fileExt = publishUploadFile.name.split('.').pop();
        const fileName = `${org.id}/${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('generations')
          .upload(fileName, publishUploadFile);
          
        if (uploadError) throw new Error('Erro ao fazer upload do vídeo: ' + uploadError.message);
        
        const { data: { publicUrl } } = supabase.storage
          .from('generations')
          .getPublicUrl(fileName);
        
        thumbnailUrl = publicUrl;
      }

      const { error } = await supabase.from('marketplace_items').insert({
        org_id: org.id,
        author_id: user.id,
        title: `Tag: ${publishingTag.label}`,
        description: (publishingTag.prompt_json as any)?.instruction || `Tag de cena - ${publishingTag.label}`,
        type: 'scene_tag',
        content: JSON.stringify(publishingTag.prompt_json),
        category: 'geral',
        price_credits: priceNum,
        tags: [publishingTag.category, publishingTag.tag_key],
        thumbnail_url: thumbnailUrl,
      });
      if (error) throw error;
      setFeedback({ type: 'success', message: `"${publishingTag.label}" publicada no Marketplace!` });
      setPublishingTag(null);
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setPublishingId(null);
    }
  };

  const catMeta = (cat: string) => CATEGORY_META[cat] || { icon: <Tag size={12} />, color: '#888', label: cat };

  const renderTagChip = (tag: SceneTag, showActions = false) => {
    const cm = catMeta(tag.category);
    return (
      <div
        key={tag.id}
        style={{
          background: 'rgba(25,27,33,0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: '14px',
          border: `1px solid ${cm.color}25`,
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.6rem',
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${cm.color}60`; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${cm.color}25`; }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '10px',
              background: `${cm.color}15`, color: cm.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${cm.color}30`,
            }}>
              {ICON_MAP[tag.icon_name] || <Tag size={14} />}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-light)' }}>{tag.label}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{tag.tag_key}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{
              padding: '2px 8px', borderRadius: '12px', fontSize: '0.62rem', fontWeight: 600,
              background: `${cm.color}18`, color: cm.color, border: `1px solid ${cm.color}30`,
            }}>
              {cm.label}
            </span>
            {tag.is_system && (
              <span style={{
                padding: '2px 8px', borderRadius: '12px', fontSize: '0.62rem', fontWeight: 600,
                background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)',
              }}>
                Sistema
              </span>
            )}
          </div>
        </div>

        {/* Instruction preview */}
        <div style={{
          fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.45,
          padding: '0.5rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px',
          maxHeight: '60px', overflow: 'hidden',
        }}>
          {(tag.prompt_json as any)?.instruction || 'Sem instrução definida'}
        </div>

        {/* Actions */}
        {showActions && (
          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.2rem' }}>
            <button
              onClick={() => setJsonViewTag(tag)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                padding: '0.4rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(255,255,255,0.03)', color: 'var(--text-muted)', fontSize: '0.7rem',
                cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
              }}
            >
              <Code2 size={12} /> JSON
            </button>
            {tag.is_system && isAdmin && (
              <button
                onClick={() => handleHideSystemTag(tag)}
                title="Ocultar para esta organização"
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                  padding: '0.4rem', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.2)',
                  background: 'rgba(245,158,11,0.06)', color: '#fbbf24', fontSize: '0.7rem',
                  cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
                }}
              >
                <EyeOff size={12} /> Ocultar
              </button>
            )}
            {!tag.is_system && isAdmin && (
              <>
                <button
                  onClick={() => openEditor(tag)}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    padding: '0.4rem', borderRadius: '8px', border: '1px solid rgba(99,102,241,0.2)',
                    background: 'rgba(99,102,241,0.06)', color: '#818cf8', fontSize: '0.7rem',
                    cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
                  }}
                >
                  <Edit3 size={12} /> Editar
                </button>
                {MARKETPLACE_ENABLED && (
                <button
                  onClick={() => handlePublishToMarketplace(tag)}
                  disabled={publishingId === tag.id}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    padding: '0.4rem', borderRadius: '8px', border: '1px solid rgba(168,85,247,0.2)',
                    background: 'rgba(168,85,247,0.06)', color: '#c084fc', fontSize: '0.7rem',
                    cursor: publishingId === tag.id ? 'wait' : 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
                  }}
                >
                  <ShoppingCart size={12} /> {publishingId === tag.id ? 'Publicando...' : 'Marketplace'}
                </button>
                )}
                <button
                  onClick={() => handleDelete(tag)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0.4rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.2)',
                    background: 'rgba(239,68,68,0.06)', color: '#f87171', fontSize: '0.7rem',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Tags de Cena</h1>
          <p className="subtitle">Gerencie os prompts estruturados para geração de vídeos</p>
        </div>
        {isAdmin && (
          <button className="btn-primary" style={{ width: 'auto', padding: '0.7rem 1.5rem' }} onClick={() => openEditor()}>
            <Plus size={18} /> Nova Tag
          </button>
        )}
      </div>

      {feedback && (
        <div className={`alert-banner ${feedback.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {feedback.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{feedback.message}</span>
          <button className="alert-dismiss" onClick={() => setFeedback(null)}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="marketplace-filters">
        <div className="search-bar">
          <Search size={18} />
          <input
            type="text" placeholder="Buscar tags..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="form-select form-select-sm" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="all">Todas Categorias</option>
          {Object.entries(CATEGORY_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>
      ) : (
        <>
          {/* System Tags — Collapsible */}
          {allSystemTags.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <button
                onClick={() => toggleSection('system')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem 0',
                  marginBottom: collapsedSections['system'] ? '0' : '0.75rem',
                }}
              >
                {collapsedSections['system']
                  ? <ChevronRight size={18} style={{ color: '#10b981' }} />
                  : <ChevronDown size={18} style={{ color: '#10b981' }} />}
                <ShieldCheck size={16} style={{ color: '#10b981' }} />
                <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                  Tags Padrão do Sistema ({visibleSystemTags.length})
                </span>
                {hiddenSystemTags.length > 0 && (
                  <span style={{
                    padding: '1px 8px', borderRadius: '10px', fontSize: '0.6rem', fontWeight: 600,
                    background: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)',
                    marginLeft: '0.3rem',
                  }}>
                    {hiddenSystemTags.length} oculta{hiddenSystemTags.length > 1 ? 's' : ''}
                  </span>
                )}
              </button>

              {!collapsedSections['system'] && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.75rem' }}>
                    {visibleSystemTags.map(t => renderTagChip(t, true))}
                  </div>

                  {/* Hidden system tags section */}
                  {hiddenSystemTags.length > 0 && isAdmin && (
                    <div style={{ marginTop: '1rem' }}>
                      <button
                        onClick={() => toggleSection('hidden')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%',
                          background: 'none', border: 'none', cursor: 'pointer', padding: '0.3rem 0',
                          marginBottom: collapsedSections['hidden'] ? '0' : '0.5rem',
                        }}
                      >
                        {collapsedSections['hidden']
                          ? <ChevronRight size={14} style={{ color: '#fbbf24' }} />
                          : <ChevronDown size={14} style={{ color: '#fbbf24' }} />}
                        <EyeOff size={13} style={{ color: '#fbbf24' }} />
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#fbbf24' }}>
                          Tags Ocultas ({hiddenSystemTags.length})
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRestoreAllSystemTags(); }}
                          style={{
                            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '3px',
                            padding: '2px 10px', borderRadius: '10px', fontSize: '0.62rem', fontWeight: 600,
                            border: '1px solid rgba(16,185,129,0.2)', background: 'rgba(16,185,129,0.06)',
                            color: '#10b981', cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          <RotateCcw size={10} /> Restaurar Todas
                        </button>
                      </button>

                      {!collapsedSections['hidden'] && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.75rem', opacity: 0.6 }}>
                          {hiddenSystemTags.map(tag => {
                            const cm = catMeta(tag.category);
                            return (
                              <div
                                key={tag.id}
                                style={{
                                  background: 'rgba(25,27,33,0.5)', borderRadius: '14px',
                                  border: '1px dashed rgba(245,158,11,0.2)', padding: '0.75rem',
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div style={{
                                    width: 28, height: 28, borderRadius: '8px',
                                    background: `${cm.color}10`, color: cm.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: `1px solid ${cm.color}20`,
                                  }}>
                                    {ICON_MAP[tag.icon_name] || <Tag size={14} />}
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{tag.label}</div>
                                    <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', fontFamily: 'monospace', opacity: 0.6 }}>{tag.tag_key}</div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRestoreSystemTag(tag.id)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                    padding: '4px 10px', borderRadius: '8px', fontSize: '0.65rem', fontWeight: 600,
                                    border: '1px solid rgba(16,185,129,0.25)', background: 'rgba(16,185,129,0.08)',
                                    color: '#10b981', cursor: 'pointer', fontFamily: 'inherit',
                                  }}
                                >
                                  <RotateCcw size={10} /> Restaurar
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Org Tags — Collapsible */}
          <div>
            <button
              onClick={() => toggleSection('org')}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%',
                background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem 0',
                marginBottom: collapsedSections['org'] ? '0' : '0.75rem',
              }}
            >
              {collapsedSections['org']
                ? <ChevronRight size={18} style={{ color: '#818cf8' }} />
                : <ChevronDown size={18} style={{ color: '#818cf8' }} />}
              <Tag size={16} style={{ color: '#818cf8' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                Tags da Organização ({orgTags.length})
              </span>
            </button>

            {!collapsedSections['org'] && (
              <>
                {orgTags.length === 0 ? (
                  <div className="glass-card wide-card">
                    <div className="empty-state">
                      <Tag size={48} />
                      <p>{isAdmin ? 'Crie tags customizadas para sua equipe!' : 'Nenhuma tag customizada criada ainda.'}</p>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.75rem' }}>
                    {orgTags.map(t => renderTagChip(t, true))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* JSON View Modal */}
      {jsonViewTag && (
        <ModalOverlay onClose={() => setJsonViewTag(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Code2 size={20} /> {jsonViewTag.label} — Prompt JSON
              </h2>
              <button className="modal-close" onClick={() => setJsonViewTag(null)}><X size={20} /></button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '3px 10px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 600,
                  background: `${catMeta(jsonViewTag.category).color}15`,
                  color: catMeta(jsonViewTag.category).color,
                  border: `1px solid ${catMeta(jsonViewTag.category).color}30`,
                }}>
                  {catMeta(jsonViewTag.category).label}
                </span>
                <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.7rem', fontFamily: 'monospace', background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)' }}>
                  {jsonViewTag.tag_key}
                </span>
              </div>
              <pre style={{
                background: 'rgba(0,0,0,0.4)', borderRadius: '12px', padding: '1rem',
                color: '#a5f3fc', fontSize: '0.78rem', lineHeight: 1.6,
                overflow: 'auto', maxHeight: '400px', border: '1px solid rgba(255,255,255,0.06)',
                fontFamily: '"Fira Code", "Cascadia Code", monospace',
              }}>
                {JSON.stringify(jsonViewTag.prompt_json, null, 2)}
              </pre>
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(jsonViewTag.prompt_json, null, 2));
                    setFeedback({ type: 'success', message: 'JSON copiado!' });
                  }}
                >
                  Copiar JSON
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Create/Edit Modal */}
      {showEditor && (
        <ModalOverlay onClose={() => setShowEditor(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTag ? 'Editar Tag' : 'Nova Tag de Cena'}</h2>
              <button className="modal-close" onClick={() => setShowEditor(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Label</label>
                  <input type="text" required placeholder="Ex: Câmera Aérea" value={form.label}
                    onChange={e => setForm({ ...form, label: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Chave Única (tag_key)</label>
                  <input type="text" required placeholder="Ex: cam_aerial" value={form.tag_key}
                    onChange={e => setForm({ ...form, tag_key: e.target.value })}
                    disabled={!!editingTag}
                    style={{ opacity: editingTag ? 0.5 : 1 }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Categoria</label>
                  <select className="form-select" value={form.category} onChange={e => {
                    const cat = e.target.value;
                    const meta = CATEGORY_META[cat];
                    setForm({ ...form, category: cat, color: meta?.color || '#6366f1' });
                  }}>
                    {Object.entries(CATEGORY_META).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Ícone</label>
                  <select className="form-select" value={form.icon_name} onChange={e => setForm({ ...form, icon_name: e.target.value })}>
                    {Object.keys(ICON_MAP).map(k => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ width: 80 }}>
                  <label>Cor</label>
                  <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                    style={{ width: '100%', height: 38, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'transparent', cursor: 'pointer' }}
                  />
                </div>
              </div>

              {/* Preview */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Preview:</span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '4px 12px', borderRadius: '16px', fontSize: '0.72rem', fontWeight: 500,
                  border: `1px solid ${form.color}`, background: `${form.color}18`, color: form.color,
                }}>
                  {ICON_MAP[form.icon_name] || <Tag size={12} />} {form.label || 'Nome da Tag'}
                </span>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Wand2 size={14} /> Gerar JSON com IA
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    placeholder="Descreva em linguagem natural: ex. 'câmera aérea com drone', 'iluminação neon noturna'"
                    value={promptDescription}
                    onChange={e => setPromptDescription(e.target.value)}
                    style={{ flex: 1 }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleGenerateWithAI(); } }}
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={generatingJson || !promptDescription.trim()}
                    onClick={handleGenerateWithAI}
                    style={{
                      width: 'auto', padding: '0.6rem 1.2rem', fontSize: '0.78rem',
                      display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                      opacity: generatingJson ? 0.7 : 1,
                    }}
                  >
                    {generatingJson ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                    {generatingJson ? 'Gerando...' : 'Gerar'}
                  </button>
                </div>
                <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  A IA preencherá todos os campos automaticamente com base na descrição
                </span>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Code2 size={14} /> Prompt JSON Estruturado
                </label>
                <textarea
                  className="form-textarea"
                  required
                  rows={12}
                  value={form.prompt_json}
                  onChange={e => setForm({ ...form, prompt_json: e.target.value })}
                  style={{
                    fontFamily: '"Fira Code", "Cascadia Code", monospace',
                    fontSize: '0.78rem', lineHeight: 1.5, color: '#a5f3fc',
                    background: 'rgba(0,0,0,0.3)', borderColor: 'rgba(255,255,255,0.08)',
                  }}
                />
                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                  Defina instruction, parameters, weight e conflicts_with
                </span>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowEditor(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0.7rem 2rem' }}>
                  <Save size={16} /> {editingTag ? 'Salvar' : 'Criar Tag'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {deletingTag && (
        <ModalOverlay onClose={() => setDeletingTag(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Excluir Tag</h3>
              <button className="close-btn" onClick={() => setDeletingTag(null)}><X size={20} /></button>
            </div>
            
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                padding: '1rem', borderRadius: '12px', display: 'flex', gap: '0.75rem', color: '#ef4444'
              }}>
                <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                  <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Atenção: Ação Irreversível</strong>
                  Tem certeza que deseja excluir a tag <strong>"{deletingTag.label}"</strong>?
                  <br /><br />
                  Se esta tag estiver publicada no Marketplace, ela será ocultada para novas compras. <strong>A organização deve estar ciente de que as compras já realizadas por outras empresas serão mantidas</strong> para garantir a integridade dos projetos delas.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="button" className="btn-secondary" onClick={() => setDeletingTag(null)}>Cancelar</button>
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={handleConfirmDelete}
                  style={{ background: '#ef4444', borderColor: '#ef4444' }}
                >
                  <Trash2 size={16} /> Excluir Definitivamente
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Publish to Marketplace Modal */}
      {MARKETPLACE_ENABLED && publishingTag && (
        <ModalOverlay onClose={() => setPublishingTag(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Publicar Tag no Marketplace</h3>
              <button className="close-btn" onClick={() => setPublishingTag(null)}><X size={20} /></button>
            </div>
            
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              
              {/* Warning Banner */}
              <div style={{
                background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)',
                padding: '1rem', borderRadius: '12px', display: 'flex', gap: '0.75rem', color: '#fbbf24'
              }}>
                <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                  <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Atenção: Acesso Público</strong>
                  Ao publicar, o conteúdo do seu prompt JSON e o vídeo anexado ficarão visíveis para outras empresas na plataforma, que poderão comprar e usar esta tag.
                </div>
              </div>

              {/* Price */}
              <div className="form-group">
                <label>Preço de Venda (em créditos)</label>
                <input 
                  type="number" 
                  min="1" 
                  step="1"
                  className="form-input" 
                  value={publishPrice}
                  onChange={e => setPublishPrice(e.target.value)}
                />
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Você receberá uma comissão em créditos cada vez que outra organização usar sua tag.</span>
              </div>

              {/* Media Attachment */}
              <div className="form-group">
                <label>Vídeo de Exemplo (Opcional, porém recomendado)</label>
                
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button 
                    type="button"
                    onClick={() => setPublishMediaSource('none')}
                    style={{
                      flex: 1, padding: '0.6rem', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      background: publishMediaSource === 'none' ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                      color: publishMediaSource === 'none' ? '#818cf8' : 'var(--text-muted)',
                      border: `1px solid ${publishMediaSource === 'none' ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.08)'}`
                    }}
                  >
                    <X size={14} /> Nenhum
                  </button>
                  <button 
                    type="button"
                    onClick={() => setPublishMediaSource('gallery')}
                    style={{
                      flex: 1, padding: '0.6rem', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      background: publishMediaSource === 'gallery' ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                      color: publishMediaSource === 'gallery' ? '#818cf8' : 'var(--text-muted)',
                      border: `1px solid ${publishMediaSource === 'gallery' ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.08)'}`
                    }}
                  >
                    <Film size={14} /> Da Galeria
                  </button>
                  <button 
                    type="button"
                    onClick={() => setPublishMediaSource('upload')}
                    style={{
                      flex: 1, padding: '0.6rem', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      background: publishMediaSource === 'upload' ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                      color: publishMediaSource === 'upload' ? '#818cf8' : 'var(--text-muted)',
                      border: `1px solid ${publishMediaSource === 'upload' ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.08)'}`
                    }}
                  >
                    <Upload size={14} /> Upload
                  </button>
                </div>

                {publishMediaSource === 'gallery' && (
                  <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    {orgAssets.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                        Nenhum vídeo disponível na galeria da organização.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem', maxHeight: '250px', overflowY: 'auto', padding: '0.2rem' }}>
                        {orgAssets.map(asset => (
                          <div 
                            key={asset.id} 
                            onClick={() => setPublishSelectedAsset(asset.id)}
                            style={{ 
                              position: 'relative', height: '100px', background: '#000', borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                              border: publishSelectedAsset === asset.id ? '2px solid #818cf8' : '1px solid rgba(255,255,255,0.1)'
                            }}
                          >
                            <video src={asset.video_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                            {publishSelectedAsset === asset.id && (
                              <div style={{ position: 'absolute', top: 5, right: 5, background: '#818cf8', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CheckCircle2 size={12} color="#fff" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {publishMediaSource === 'upload' && (
                  <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1.5rem', borderRadius: '12px', border: '1px dashed rgba(255,255,255,0.15)', textAlign: 'center' }}>
                    <input 
                      type="file" 
                      accept="video/mp4,video/webm" 
                      id="publish-upload-video" 
                      style={{ display: 'none' }}
                      onChange={e => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          if (file.size > 50 * 1024 * 1024) {
                            setFeedback({ type: 'error', message: 'O arquivo excede o limite de 50MB.' });
                            return;
                          }
                          if (!['video/mp4', 'video/webm'].includes(file.type)) {
                            setFeedback({ type: 'error', message: 'Formato de arquivo inválido. Apenas MP4 ou WebM são permitidos.' });
                            return;
                          }
                          setPublishUploadFile(file);
                        }
                      }}
                    />
                    <label htmlFor="publish-upload-video" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ width: 48, height: 48, background: 'rgba(99,102,241,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#818cf8' }}>
                        <Upload size={24} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: '0.25rem' }}>Clique para selecionar arquivo</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>MP4 ou WebM (Max 50MB)</div>
                      </div>
                    </label>
                    {publishUploadFile && (
                      <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '8px', color: '#10b981', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <Video size={16} /> {publishUploadFile.name} selecionado
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="button" className="btn-secondary" onClick={() => setPublishingTag(null)}>Cancelar</button>
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={handleConfirmPublish}
                  disabled={publishingId === publishingTag.id || (publishMediaSource === 'gallery' && !publishSelectedAsset) || (publishMediaSource === 'upload' && !publishUploadFile)}
                  style={{ width: 'auto', padding: '0.7rem 2rem' }}
                >
                  <ShoppingCart size={16} /> 
                  {publishingId === publishingTag.id ? 'Publicando...' : 'Confirmar Publicação'}
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}
